import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Check, Mail, MessageCircle } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Logo from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Card, CardContent } from "@/components/ui/card";

const PLANS = [
  {
    id: "junior",
    name: "Júnior",
    price: 70,
    tests: 16,
    accent: "border-slate-200",
    highlight: false,
  },
  {
    id: "pleno",
    name: "Pleno",
    price: 100,
    tests: 24,
    accent: "border-[#FF8A50]",
    highlight: true,
  },
  {
    id: "senior",
    name: "Sênior",
    price: 120,
    tests: 32,
    accent: "border-[#7CB342]",
    highlight: false,
  },
];

const WA_LINK = "https://wa.me/5519933004705?text=" + encodeURIComponent(
  "Olá! Vim pelo Sensorize STAT e gostaria de assinar um plano."
);

export default function Login() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.user, data.access_token);
      toast.success("Autenticado com sucesso");
      navigate("/dashboard", { replace: true });
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F8FAFC] py-10 px-4">
      <div className="max-w-6xl mx-auto">
        <div className="flex justify-center mb-8">
          <Logo className="h-12" />
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8 items-start">
          {/* LEFT — Login */}
          <div className="lg:col-span-2 lg:sticky lg:top-8">
            <Card className="border-slate-200 shadow-sm">
              <CardContent className="p-8">
                <h1 className="text-2xl font-heading font-bold text-slate-900 mb-1">Entrar</h1>
                <p className="text-sm text-slate-500 mb-6">
                  Digite seu email e senha para acessar o Sensorize STAT.
                </p>
                <form onSubmit={submit} className="space-y-4">
                  <div>
                    <Label htmlFor="email" className="text-slate-700">Email</Label>
                    <Input
                      id="email" type="email" required
                      value={email} onChange={(e) => setEmail(e.target.value)}
                      placeholder="seu@email.com"
                      data-testid="login-email-input" className="mt-1"
                    />
                  </div>
                  <div>
                    <Label htmlFor="password" className="text-slate-700">Senha</Label>
                    <Input
                      id="password" type="password" required
                      value={password} onChange={(e) => setPassword(e.target.value)}
                      data-testid="login-password-input" className="mt-1"
                    />
                  </div>
                  <Button
                    type="submit" disabled={loading}
                    data-testid="login-submit-button"
                    className="w-full bg-[#FF8A50] hover:bg-[#E65C00] text-white font-semibold"
                  >
                    {loading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    Entrar
                  </Button>
                </form>
                <div className="mt-6 flex items-center justify-between text-sm">
                  <Link to="/register" className="text-[#FF8A50] hover:underline font-medium" data-testid="link-to-register">
                    Criar conta
                  </Link>
                  <span className="text-slate-400">A sessão expira em 20h</span>
                </div>
              </CardContent>
            </Card>

            {/* Contact card */}
            <Card className="border-slate-200 shadow-sm mt-4" data-testid="contact-card">
              <CardContent className="p-6">
                <h2 className="text-sm font-heading font-bold text-slate-900 mb-3 uppercase tracking-widest">Contato</h2>
                <a
                  href={WA_LINK} target="_blank" rel="noopener noreferrer"
                  data-testid="contact-whatsapp"
                  className="flex items-center gap-3 py-2 text-slate-800 hover:text-[#128C7E] transition-colors"
                >
                  <span className="w-9 h-9 rounded-full bg-[#25D366]/15 text-[#128C7E] flex items-center justify-center">
                    <MessageCircle className="w-4 h-4" />
                  </span>
                  <div className="text-sm">
                    <div className="text-slate-500 text-xs">WhatsApp</div>
                    <div className="font-semibold">(19) 9 3300-4705</div>
                  </div>
                </a>
                <a
                  href="mailto:sensorize@outlook.com.br"
                  data-testid="contact-email"
                  className="flex items-center gap-3 py-2 text-slate-800 hover:text-[#FF8A50] transition-colors"
                >
                  <span className="w-9 h-9 rounded-full bg-[#FFF1E6] text-[#FF8A50] flex items-center justify-center">
                    <Mail className="w-4 h-4" />
                  </span>
                  <div className="text-sm">
                    <div className="text-slate-500 text-xs">Email de suporte</div>
                    <div className="font-semibold">sensorize@outlook.com.br</div>
                  </div>
                </a>
              </CardContent>
            </Card>

            {/* Positioning / value proposition */}
            <Card className="border-slate-200 shadow-sm mt-4" data-testid="value-prop-card">
              <CardContent className="p-6">
                <h2 className="text-sm font-heading font-bold text-slate-900 mb-3 uppercase tracking-widest">
                  Por que Sensorize STAT?
                </h2>
                <p className="text-sm text-slate-700 leading-relaxed">
                  Diferente de softwares como <strong>SAS</strong>, <strong>R</strong> ou <strong>Fizz</strong>,
                  nossa solução <strong>elimina a complexidade excessiva</strong> e os <strong>custos elevados</strong> de
                  ferramentas como <strong>EyeQuestion</strong>, <strong>RedJade</strong> e <strong>Compusense</strong>,
                  que chegam a <strong>milhares de reais em um único mês</strong>.
                </p>
                <p className="text-sm text-slate-700 leading-relaxed mt-3">
                  O software está em <strong>constante evolução</strong>, incorporando feedback dos usuários, novas
                  tecnologias e avanços científicos, para garantir que você tenha sempre uma ferramenta moderna,
                  acessível e confiável.
                </p>
                <div className="mt-4 rounded-lg border border-[#FF8A50]/30 bg-[#FFF7F0] p-3">
                  <p className="text-sm text-slate-800 leading-relaxed">
                    É necessário que o usuário tenha <strong>conhecimento prévio em Análise Sensorial e Tratamento
                    de Dados</strong>.
                  </p>
                  <p className="text-sm text-slate-700 leading-relaxed mt-2">
                    Informações sobre o <strong>Curso de Introdução à Análise Sensorial</strong>:{" "}
                    <a
                      href="https://sensorizepesquisas.com.br/#cursos"
                      target="_blank"
                      rel="noopener noreferrer"
                      data-testid="course-link"
                      className="text-[#E65C00] font-semibold hover:underline"
                    >
                      sensorizepesquisas.com.br/#cursos
                    </a>
                  </p>
                </div>
              </CardContent>
            </Card>
          </div>

          {/* RIGHT — Plans */}
          <div className="lg:col-span-3">
            <div className="mb-4">
              <p className="text-xs font-semibold uppercase tracking-[0.15em] text-[#7CB342]">Assine o Sensorize STAT</p>
              <h2 className="text-3xl sm:text-4xl font-heading font-extrabold text-slate-900 mt-1">
                Escolha seu plano mensal
              </h2>
              <p className="text-slate-600 mt-2">
                Todos os planos incluem os módulos <strong>ANOVA</strong> e <strong>Análise Sensorial</strong>, exportação PDF/Excel/CSV e histórico completo.
              </p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="plans-grid">
              {PLANS.map((p) => (
                <Card key={p.id} className={`border-2 ${p.accent} transition-transform hover:-translate-y-1`} data-testid={`plan-${p.id}`}>
                  <CardContent className="p-6">
                    {p.highlight && (
                      <span className="inline-block text-[10px] font-bold uppercase tracking-widest text-white bg-[#FF8A50] rounded-full px-2 py-0.5 mb-3">
                        Mais escolhido
                      </span>
                    )}
                    <div className="text-slate-500 text-xs uppercase font-semibold tracking-widest">Plano</div>
                    <div className="text-2xl font-heading font-extrabold text-slate-900 mt-1">{p.name}</div>
                    <div className="mt-4 flex items-baseline gap-1">
                      <span className="text-3xl font-heading font-extrabold text-slate-900">R$ {p.price}</span>
                      <span className="text-slate-500 text-sm">/ mês</span>
                    </div>
                    <div className="text-[11px] uppercase tracking-widest font-semibold text-slate-500 mt-1">
                      Cobrança mensal recorrente
                    </div>
                    <div className="mt-3 text-sm text-slate-700">
                      <strong>{p.tests} testes</strong> por mês
                    </div>
                    <ul className="mt-4 space-y-2 text-sm text-slate-700">
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#7CB342] mt-0.5 flex-shrink-0" />
                        <span>Módulos <strong>ANOVA</strong> e <strong>Sensorial</strong> completos</span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#7CB342] mt-0.5 flex-shrink-0" />
                        <span>Exportação <strong>PDF, Excel e CSV</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#7CB342] mt-0.5 flex-shrink-0" />
                        <span>Testes não utilizados <strong>acumulam para o mês seguinte</strong></span>
                      </li>
                      <li className="flex items-start gap-2">
                        <Check className="w-4 h-4 text-[#7CB342] mt-0.5 flex-shrink-0" />
                        <span><strong>Cancele quando quiser</strong>, sem multa</span>
                      </li>
                    </ul>
                    <Button
                      asChild
                      className={`mt-5 w-full font-semibold ${
                        p.highlight
                          ? "bg-[#FF8A50] hover:bg-[#E65C00] text-white"
                          : "bg-slate-900 hover:bg-slate-800 text-white"
                      }`}
                    >
                      <a
                        href={"https://wa.me/5519933004705?text=" + encodeURIComponent(`Olá! Quero assinar o Plano ${p.name} (R$ ${p.price}/mês).`)}
                        target="_blank" rel="noopener noreferrer"
                        data-testid={`subscribe-${p.id}`}
                      >
                        Quero este plano
                      </a>
                    </Button>
                  </CardContent>
                </Card>
              ))}
            </div>

            {/* Info boxes */}
            <div className="mt-5 grid grid-cols-1 md:grid-cols-3 gap-4">
              <div className="rounded-lg border border-[#FF8A50]/40 bg-[#FFF4EA] p-4" data-testid="recurring-note">
                <div className="text-[11px] uppercase tracking-widest font-bold text-[#E65C00] mb-1">Recorrência mensal</div>
                <p className="text-sm text-slate-700">
                  <strong>Cobrança automática todo mês</strong> no mesmo dia da assinatura. Sem taxa de adesão e sem multa.
                </p>
              </div>
              <div className="rounded-lg border border-[#7CB342]/40 bg-[#F5FAEE] p-4" data-testid="accumulation-note">
                <div className="text-[11px] uppercase tracking-widest font-bold text-[#558B2F] mb-1">Créditos acumulativos</div>
                <p className="text-sm text-slate-700">
                  Testes não utilizados <strong>acumulam automaticamente para o mês seguinte</strong>. Você não perde o que já pagou.
                </p>
              </div>
              <div className="rounded-lg border border-slate-200 bg-white p-4" data-testid="cancellation-note">
                <div className="text-[11px] uppercase tracking-widest font-bold text-slate-600 mb-1">Cancele quando quiser</div>
                <p className="text-sm text-slate-700">
                  O cancelamento <strong>interrompe a próxima cobrança</strong> e <strong>mantém seu acesso ativo até o fim do mês vigente</strong>.
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
